import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";

import AmoTableWithFilters from "components/table/AmoTableWithFilters";
import { displayTypes, tableDefaultActions } from "constants/amoTableConstants";
import { amoUserSpecialRoles } from "constants/amoTableDropdownOptions";
import { errorMessages } from "constants/errorMessages";
import { snackbarTypes } from "constants/snackbar";
import { useSnackbar } from "contexts/SnackbarContext";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useAmoUserEnabledOrAdminHooks } from "./hooks/userDataHooks";
import { formatISODateDashStyle } from "utils/date";
import { booleanToTextOrUndefined } from "utils/string";
import { exportToExcel } from "utils/export";
import CheckedIcon from "./CheckedIcon";

/**
 * A table component for displaying AMO users
 *
 * @param {object} props
 * @returns {React.Component} The table component
 */
const AmoUserTable = (props) => {
  const { id, tableTitleComponent } = props;

  const history = useHistory();
  const location = useLocation();
  const globalClasses = useGlobalStyles();
  const { showSnackbar } = useSnackbar();

  const [focusedItem, setFocusedItem] = useState({});
  const rowRefs = useRef({});
  const filteredData = useRef([]);

  const {
    data: userData,
    isFetching: dataFetching,
  } = useAmoUserEnabledOrAdminHooks();

  const tableColumns = [
    {
      fieldKey: "name",
      label: "Name",
      minWidth: "11rem",
      customRender: (user) =>
        user.specialRole !== "Admin" ? (
          <Link
            to={`/users/amo/${user.id}${location.search}`}
            ref={(element) => {
              rowRefs.current[user.id] = element;
            }}
            onFocus={() => setFocusedItem({ id: user.id })}
            className={globalClasses.tableActionCell}
          >
            {user.name}
          </Link>
        ) : (
          <Typography variant="default" style={{ fontWeight: "bold" }}>
            {user.name}
          </Typography>
        ),
      isCustom: true,
    },
    {
      fieldKey: "assignedProjects",
      label: "Projects Assigned",
      horizontalAlign: "left",
      displayType: displayTypes.number,
      customRender: (user) =>
        user.specialRole !== "Admin" ? (
          <Link
            to={`/reviews/projects/?filter_assigneeName=${user.name}-true${location.search}`}
            ref={(element) => {
              rowRefs.current[user.id] = element;
            }}
            onFocus={() => setFocusedItem({ id: user.id })}
            onClick={(elem) => elem.stopPropagation()}
            className={globalClasses.tableActionCell}
          >
            {user.assignedProjects}
          </Link>
        ) : (
          <Typography variant="default" style={{ fontWeight: "bold" }}>
            {user.assignedProjects}
          </Typography>
        ),
      isCustom: true,
    },
    {
      fieldKey: "assignedFinancials",
      label: "Financials Assigned",
      horizontalAlign: "left",
      displayType: displayTypes.number,
      customRender: (user) =>
        user.specialRole !== "Admin" ? (
          <Link
            to={`/reviews/financial/?filter_assignedTo=${user.name}-true${location.search}`}
            ref={(element) => {
              rowRefs.current[user.id] = element;
            }}
            onFocus={() => setFocusedItem({ id: user.id })}
            onClick={(elem) => elem.stopPropagation()}
            className={globalClasses.tableActionCell}
          >
            {user.assignedFinancials}
          </Link>
        ) : (
          <Typography variant="default" style={{ fontWeight: "bold" }}>
            {user.assignedFinancials}
          </Typography>
        ),
      isCustom: true,
    },
    {
      fieldKey: "specialRole",
      label: "Special Role",
      dropdownOptions: amoUserSpecialRoles,
      displayType: displayTypes.dropdown,
    },
    {
      fieldKey: "lastLoginDate",
      label: "Last Login",
      customRender: (item) =>
        item.lastLoginDate !== "1970-01-01T00:00:00" ? (
          <Typography>
            <Grid item xs>
              {formatISODateDashStyle(item.lastLoginDate)}{" "}
            </Grid>
          </Typography>
        ) : (
          <></>
        ),
      isCustom: true,
      isDate: true,
      displayType: displayTypes.date,
    },
    {
      fieldKey: "enabled",
      label: "Enabled",
      customRender: (item) => <CheckedIcon checked={item.enabled} />,
      customExport: (item) => booleanToTextOrUndefined(item.enabled) ?? "",
      displayType: displayTypes.boolean,
      isCustom: true,
      horizontalAlign: "center",
    },
  ];

  const handleItemClick = (user) => {
    history.push(`/users/amo/${user.id}${location.search}`);
  };

  const tableActions = {
    titleComponent: tableTitleComponent,
    actions: [tableDefaultActions.search, tableDefaultActions.export],
    actionsCustomConfigs: {
      [tableDefaultActions.export]: {
        onClick: () => {
          try {
            const exportData = JSON.parse(
              JSON.stringify(filteredData.current)
            ).map((elem) => ({
              ...elem,
              specialRole:
                amoUserSpecialRoles[elem.specialRole] || elem.specialRole,
              lastLoginDate:
                elem.lastLoginDate === "1970-01-01T00:00:00"
                  ? ""
                  : elem.lastLoginDate,
            }));
            exportToExcel(exportData, tableColumns, "AMO Staff");
          } catch (error) {
            console.error(error);

            showSnackbar(
              errorMessages.creatingSpreadsheet,
              snackbarTypes.error
            );
          }
        },
      },
    },
  };

  return (
    <AmoTableWithFilters
      id={id}
      testId="amoUserTable"
      items={dataFetching ? [] : userData}
      columns={tableColumns}
      defaultOrderBy="name"
      onItemClick={handleItemClick}
      onFilteredDataChanged={(value) => {
        filteredData.current = value;
      }}
      onScroll={(item) => {
        rowRefs.current?.[item.id]?.focus();
      }}
      focusedItem={focusedItem}
      actionsProps={tableActions}
      isLoading={dataFetching}
    />
  );
};

// set the prop-types for this component
AmoUserTable.propTypes = {
  id: PropTypes.string,
  tableTitleComponent: PropTypes.node,
};

AmoUserTable.defaultProps = {
  id: "amo-user-table",
  tableTitleComponent: null,
};

export default AmoUserTable;
